/**
 * Announcing a leave request — once, after it is real, and never at the cost of the record.
 *
 * ── Why this sits between the services and the emails ─────────────────────────────────────
 *
 * Three rules have to hold for every leave message, and each of them was previously the
 * responsibility of whichever call site remembered it:
 *
 *   ONCE        one submission is one email, however many times the caller is re-entered.
 *   AFTER       the request exists on the server before anyone is told it does.
 *   NEVER FATAL a failed email cannot make a saved request look unsaved.
 *
 * They are enforced here instead, so `LeaveService.createLeave` and
 * `LeaveApprovalService.updateLeaveStatus` each get them by calling one function.
 *
 * ── The duplicate guard ───────────────────────────────────────────────────────────────────
 *
 * Keyed on the row, not on the click: `submitted:<leave id>` and `decision:<leave id>:<status>`.
 * That is the key that survives every retry worth guarding against — React re-running an effect,
 * a double-clicked button whose handler already fired, a GraphQL client retrying a mutation it
 * believes failed, a Lambda invoked twice for one event. All of those re-announce THE SAME row,
 * and all of them are stopped by a key on its id.
 *
 * A second click that creates a genuinely second leave row is a different problem and is not
 * this guard's to solve: it has its own id, it is its own request, and one email about it is
 * correct. The submit button's `submitting` flag and the overlap validation are what stop that
 * one from happening at all.
 *
 * Deliberately in memory and not in storage. The guard exists to collapse retries of one action,
 * which all happen within seconds inside one page; persisting it would mean a leave that failed
 * to send could never be re-announced, and would put a growing list of ids in localStorage to
 * prevent an email nobody is at risk of receiving twice.
 */

import { LeaveRequest, LeaveStatus } from '../types/leave';
import { leaveDaysFor } from './leaveDuration';
import {
  emailLeaveAmended,
  emailLeaveApplied,
  emailLeaveDecision,
  emailLeaveWithdrawn,
} from './leaveEmails';

/** The announcements this browser session has already made. See the header for the keying. */
const announced = new Set<string>();

/**
 * True the first time a key is seen, false every time after.
 *
 * The key is recorded BEFORE the email is attempted, not after. A send that is still in flight
 * has to block the retry that arrives while it is in flight — recording it on success would let
 * exactly the duplicate this guards against through, since a double-click's second handler runs
 * long before the first one's network call comes back.
 */
function firstTime(key: string): boolean {
  if (announced.has(key)) return false;
  announced.add(key);
  return true;
}

/** Forget every announcement. For tests, which share a module instance across cases. */
export function resetLeaveAnnouncements(): void {
  announced.clear();
}

/** Who the request belongs to and who decides it, as the caller already knows them. */
export interface LeaveAnnouncementContext {
  managerEmail?: string;
  managerName?: string;
}

/**
 * The facts every leave email needs, read off the stored row.
 *
 * `leaveDaysFor` rather than a date subtraction: it is the single source of truth for what a
 * request costs — working days only, 0.5 for a half-day session — and it is what the balance
 * deducts. The emails used to count calendar days and add one, so a Friday-to-Monday request
 * was announced as 4 days while 2 came off the balance.
 */
function factsFrom(leave: LeaveRequest, context: LeaveAnnouncementContext) {
  return {
    employeeName: leave.employeeName || leave.employeeEmail,
    employeeEmail: leave.employeeEmail,
    leaveType: String(leave.type),
    startDate: leave.startDate,
    endDate: leave.endDate,
    days: leaveDaysFor(leave),
    session: leave.session,
    reason: leave.reason,
    department: leave.department,
    status: String(leave.status),
    managerEmail: context.managerEmail,
    managerName: context.managerName,
  };
}

/**
 * A leave request has been created — tell the employee, their manager and HR.
 *
 * Call this only once the row exists on the server. It is awaited rather than fired and
 * forgotten so a caller can be sure the attempt has finished, but it resolves either way: every
 * failure below is caught here, because the request is already saved and refusing it over a
 * bounced email would tell an employee their leave had not been filed when it had.
 */
export async function announceLeaveSubmitted(
  leave: LeaveRequest,
  context: LeaveAnnouncementContext = {}
): Promise<void> {
  if (!firstTime(`submitted:${leave.id}`)) return;
  try {
    await emailLeaveApplied(factsFrom(leave, context));
  } catch (error) {
    console.error('[LeaveNotifications] the leave was saved but could not be announced:', error);
  }
}

/**
 * A manager has decided — tell the employee, the manager and HR.
 *
 * Keyed by status as well as id, so the same request being approved after an earlier rejection
 * is a second announcement rather than a suppressed one.
 *
 * Only APPROVED and REJECTED are announced. The other statuses a request can be moved to have
 * their own messages with their own wording — a withdrawal is not a decision, and announcing
 * one as "Leave Request Rejected" would be worse than announcing nothing.
 */
export async function announceLeaveDecision(
  leave: LeaveRequest,
  status: LeaveStatus,
  decidedBy: string,
  context: LeaveAnnouncementContext & { decisionReason?: string } = {}
): Promise<void> {
  const decided = String(status).toUpperCase();
  const approved = decided === String(LeaveStatus.APPROVED).toUpperCase();
  const rejected = decided === String(LeaveStatus.REJECTED).toUpperCase();
  if (!approved && !rejected) return;

  if (!firstTime(`decision:${leave.id}:${decided}`)) return;
  try {
    await emailLeaveDecision({
      ...factsFrom({ ...leave, status }, context),
      approved,
      decidedBy,
      decisionReason: context.decisionReason,
    });
  } catch (error) {
    console.error('[LeaveNotifications] the decision was recorded but could not be announced:', error);
  }
}

/** An employee withdrew their own pending request — tell the manager and HR. */
export async function announceLeaveWithdrawn(
  leave: LeaveRequest,
  context: LeaveAnnouncementContext = {}
): Promise<void> {
  if (!firstTime(`withdrawn:${leave.id}`)) return;
  try {
    await emailLeaveWithdrawn(factsFrom({ ...leave, status: LeaveStatus.CANCELLED }, context));
  } catch (error) {
    console.error('[LeaveNotifications] the withdrawal could not be announced:', error);
  }
}

/**
 * An employee amended a request — tell the manager and HR.
 *
 * Keyed on the amended dates rather than on the id alone: amending the same request twice is
 * two real events, and the second set of dates is news to whoever has to approve them.
 */
export async function announceLeaveAmended(
  leave: LeaveRequest,
  wasApproved: boolean,
  context: LeaveAnnouncementContext = {}
): Promise<void> {
  if (!firstTime(`amended:${leave.id}:${leave.startDate}:${leave.endDate}:${leave.session || 'FULL'}`)) {
    return;
  }
  try {
    await emailLeaveAmended({ ...factsFrom(leave, context), wasApproved });
  } catch (error) {
    console.error('[LeaveNotifications] the amendment could not be announced:', error);
  }
}
